'use client'

import { useState } from 'react'
import Link from 'next/link'
import { useParams } from 'next/navigation'
import type { Mode } from '@core/types'
import type { Locale } from '@configs/i18n'
import { useSettings } from '@core/hooks/useSettings'
import themeConfig from '@configs/themeConfig'
import { getLocalizedUrl } from '@/utils/i18n'
import { toast } from 'react-toastify'
import { apiClient } from '@/libs/api'

const ForgotPassword = ({ mode: _mode }: { mode: Mode }) => {
  const { lang: locale } = useParams()
  const { settings } = useSettings()

  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorState, setErrorState] = useState<string | null>(null)
  const [successState, setSuccessState] = useState<string | null>(null)

  const loginHref = getLocalizedUrl('/login', locale as Locale)

  return (
    <div className='flex min-h-screen'>
      <div
        className={`hidden lg:flex lg:w-[55%] flex-col justify-between p-12 relative overflow-hidden ${
          settings.skin === 'bordered' ? 'border-ie' : ''
        }`}
        style={{ background: 'linear-gradient(135deg, hsl(228, 35%, 14%) 0%, hsl(234, 50%, 22%) 50%, hsl(234, 85%, 35%) 100%)' }}
      >
        <div className='relative z-10'>
          <div className='flex items-center gap-3'>
            <img
              src='/images/logo.png'
              alt={`${themeConfig.templateName} logo`}
              className='h-11 w-11 rounded-xl object-contain'
            />
            <div>
              <h1 className='text-xl font-bold text-white'>{themeConfig.templateName}</h1>
              <p className='text-xs text-blue-200/90 tracking-wide'>{themeConfig.brandTagline}</p>
            </div>
          </div>
        </div>

        <div className='relative z-10 max-w-lg'>
          <h2 className='text-4xl font-bold text-white leading-tight mb-4'>Reset your password securely</h2>
          <p className='text-lg text-blue-200/80'>
            We will send a secure reset link to your email so you can regain access quickly.
          </p>
        </div>

        <div className='relative z-10 grid grid-cols-2 gap-4 max-w-md'>
          <div className='rounded-xl border border-white/15 bg-white/5 px-4 py-3'>
            <div className='text-sm text-blue-200'>Secure flow</div>
            <div className='text-white font-semibold mt-1'>Token-based reset</div>
          </div>
          <div className='rounded-xl border border-white/15 bg-white/5 px-4 py-3'>
            <div className='text-sm text-blue-200'>Fast recovery</div>
            <div className='text-white font-semibold mt-1'>Back in minutes</div>
          </div>
        </div>

        <div className='absolute top-20 right-20 w-72 h-72 rounded-full bg-primary/10 blur-3xl' />
        <div className='absolute bottom-20 left-20 w-96 h-96 rounded-full bg-blue-500/5 blur-3xl' />
        <div className='absolute top-1/2 right-0 w-64 h-64 rounded-full bg-indigo-500/10 blur-3xl' />
      </div>

      <div className='flex flex-1 min-h-screen items-center justify-center bg-slate-100 px-4 py-10 sm:px-8'>
        <div className='w-full max-w-[440px]'>
          <div className='rounded-2xl border border-slate-200/90 bg-white p-8 shadow-[0_20px_50px_-12px_rgba(15,23,42,0.12)] sm:p-10'>
            <div className='mb-8 text-center sm:text-left'>
              <h2 className='text-[1.65rem] font-bold leading-tight tracking-tight text-slate-900 sm:text-3xl'>
                Forgot password <span className='inline-block' aria-hidden>🔒</span>
              </h2>
              <p className='mt-2 text-sm text-slate-500 sm:text-base'>
                Enter your email and we&apos;ll send you a reset link.
              </p>
            </div>

            {errorState ? (
              <div className='mb-6 rounded-xl border border-red-200 bg-red-50/90 px-4 py-3 text-sm text-red-800'>
                {errorState}
              </div>
            ) : null}

            {successState ? (
              <div className='mb-6 rounded-xl border border-emerald-200 bg-emerald-50/90 px-4 py-3 text-sm text-emerald-800'>
                {successState}
              </div>
            ) : null}

            <form
              noValidate
              autoComplete='off'
              onSubmit={async (e) => {
                e.preventDefault()
                setErrorState(null)
                setSuccessState(null)
                setIsSubmitting(true)

                try {
                  await apiClient.post('/auth/forgot-password', { email: email.trim() })
                  setSuccessState('Reset link sent. Please check your email.')
                  toast.success('Reset link sent! Check your email.')
                } catch (error: any) {
                  const msg =
                    error && typeof error === 'object' && 'data' in error
                      ? String((error as { data?: { message?: string } }).data?.message || '')
                      : ''
                  const resolved = msg || 'Failed to send reset link'
                  setErrorState(resolved)
                  toast.error(resolved)
                } finally {
                  setIsSubmitting(false)
                }
              }}
              className='space-y-5'
            >
              <div className='space-y-2'>
                <label htmlFor='forgot-email' className='text-sm font-semibold text-slate-800'>
                  Email
                </label>
                <div className='relative'>
                  <i className='ri-mail-line pointer-events-none absolute left-3.5 top-1/2 z-[1] -translate-y-1/2 text-lg text-slate-400' />
                  <input
                    id='forgot-email'
                    type='email'
                    autoComplete='email'
                    placeholder='you@company.com'
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className='h-12 w-full rounded-xl border border-slate-200 bg-white pl-11 pr-3.5 text-slate-900 shadow-sm outline-none transition-[border-color,box-shadow] placeholder:text-slate-400 focus:border-blue-600 focus:ring-4 focus:ring-blue-500/15'
                  />
                </div>
              </div>

              <button
                type='submit'
                disabled={isSubmitting || !email.trim()}
                className='h-12 w-full cursor-pointer rounded-xl bg-blue-600 text-base font-semibold text-white shadow-md shadow-blue-600/25 transition-colors hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60'
              >
                {isSubmitting ? 'Sending...' : 'Send reset link'}
              </button>

              <p className='pt-1 text-center text-sm text-slate-500'>
                <Link href={loginHref} className='inline-flex items-center gap-1 font-semibold text-blue-600 transition-colors hover:text-blue-700'>
                  <i className='ri-arrow-left-s-line' />
                  Back to Login
                </Link>
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ForgotPassword
